import { Button, Card, Center, Divider, Paper, Stack, Text, TextInput, Group, PasswordInput } from "@mantine/core";
import { useSearchParams, useRoute } from "wouter";
import { useState } from "react";
import { IconLock, IconLockCheck, IconMail } from '@tabler/icons-react';
import { PasswordInputWithRequirements } from '@/componets/password.requirements.tsx';
import { notifications } from "@mantine/notifications";
import myyentaLogo from '@/assets/my_yenta_header.png';

type AppMode = 'activate' | 'recover' | 'refresh';
const modeTitleMap: Record<AppMode, string> = {
    activate: 'Account Activation',
    recover: 'Account Recovery',
    refresh: 'Access Token Refresh',
}
const modeDescriptionMap: Record<AppMode, string> = {
    activate: `Activate your account for access to My Yenta, and start using My Yenta! All you need to do is enter a password.
    after a valid password is entered you will be redirected to the My Yenta portal.`,
    recover: `If you forgot your password, or it expired. Enter the email you are trying to recover, and we will send you a new a recovery link.`,
    refresh: `If your Magic Access Token expired, then you can get a new activation token. You must enter the same
    email that was used to create the original account. A new 24-hour token will be generated. 
    Note: if you already activated your account, but forgot
    the password. This WILL NOT work, you want you want to use the "Recover Your Account" option.`,
}
export default function App() {
    const [match, params] = useRoute('/magic/:mode?');
    const [sp, _] = useSearchParams();
    const token = sp.get('token');
    const email = sp.get('email');
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [givenEmail, setGivenEmail] = useState(email ?? '');
    const mode = (params?.mode ?? 'activate') as AppMode;
    const [isLoading, setIsLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    if (!match || (mode === 'activate' && (!email || !token))) {
        return <Center><Card withBorder w='40%' p='xl' ta='center' shadow="lg">
            <Text size="1.8rem" p='md'>Invalid magic link</Text>
            <Divider my='md' />
            <Text size='1.2rem' lh='1.5'>
                Your link seems to be missing some information. If you are unsure what to do from here, please contact
                support at <a href="mailto:accounts@myyenta.com">accounts@myyenta.com</a>
            </Text>
        </Card></Center>
    }

    const submitForm = async () => {
        setIsLoading(true);
        notifications.show({
            title: 'Sent!',
            message: 'Please wait while we process your request',
            loading: true,
            autoClose: true,
            position: 'top-center',
            color: 'green',
        });
        const reqBody = mode === 'activate' ? {
            email,
            token,
            password
        } : { email: givenEmail };
        const res = await fetch(mode === 'activate' ? '/api/magic/action' : '/api/magic/recover', {
            method: 'POST',
            body: JSON.stringify(reqBody),
        });
        if (res.ok && mode === 'activate') {
            const rb = await res.json() as {url: string};
            console.log('attempting redirect', rb)
            window.location.href = rb.url;
        } else if (res.ok && mode === 'recover') {
            setSubmitted(true);
        } else {
            notifications.show({
                title: 'Error',
                message: await res.text(),
                color: 'red',
                position: 'top-center',
                autoClose: false,
            });
            setIsLoading(false);
        }
    };

    if(submitted){
        return <Center><Card withBorder w='40%' p='xl' ta='center' shadow="lg">
            <Text size="1.8rem" p='md'>Link sent</Text>
            <Divider my='md' />
            <Text size='1.2rem' lh='1.5' ta='center' component="p">
                A link has been sent to the email you provided. If you don't see it, please check your spam folder.
                If you need help, please contact support at <a href="mailto:accounts@myyenta.com">accounts@myyenta.com</a>
            </Text>
            <Text size='1.4rem' lh='1.6' c='dimmed' ta='center' component="p">
                You can close this page now.
            </Text>
        </Card></Center>
    }

    const submitDisabled = (mode === 'activate' && (password.length < 8 || password !== passwordConfirm)) ||
        (mode === 'recover' && givenEmail.length === 0);

    return (<>
        <Paper withBorder w='60%' shadow="lg" p='md' m='auto'>
            <Stack ta='center'>
                <Group wrap="nowrap" gap='md' justify="space-between" grow>
                    <div>
                        <img src={myyentaLogo} alt='My Yenta' className="logo" />
                    </div>
                    <Text size='1.6rem' lh='2' ta='end' c='dimmed'>{modeTitleMap[mode]}</Text>
                </Group>
                <Text size='1.2rem' lh='1.4' >{modeDescriptionMap[mode]}</Text>
                <Divider my='md' />
                {mode !== 'activate' && <TextInput 
                    size="xl" 
                    label='Email' 
                    value={givenEmail} 
                    onChange={(e) => setGivenEmail(e.target.value)} 
                    leftSection={<IconMail />} 
                    description="You will only get an email if a account exists, otherwise you will receive nothing. If you don't have access to the email linked to the account, you will have to contact support to link a new email."
                />}
                {mode === 'activate' && <Group grow >
                    <PasswordInputWithRequirements
                        size="xl"
                        withAsterisk={false}
                        label='Password'
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        leftSection={<IconLock />}
                    />
                    <PasswordInput
                        size="xl"
                        label='Confirm Password'
                        ta={'left'}
                        value={passwordConfirm}
                        onChange={(e) => setPasswordConfirm(e.target.value)}
                        error={password !== passwordConfirm ? 'Passwords do not match' : undefined}
                        leftSection={<IconLockCheck />}
                        mt={5}
                    />
                </Group>}
                <Button
                    onClick={submitForm}
                    fullWidth
                    size="lg"
                    disabled={submitDisabled}
                    loading={isLoading}
                    mt='xl' mb='md'
                >Submit</Button>
            </Stack>
        </Paper>
    </>)
}